import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import apiService from '../services/apiService';

interface CompanyProfile {
  companyName: string;
  description: string | null;
  industry: string | null;
  services: string | null;
}

export function useOnboarding() {
  const { user } = useAuthStore();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const storageKey = user?.id ? `onboarding_completed_${user.id}` : 'onboarding_completed';

  // Load company profile to check completeness
  const loadCompanyProfile = useCallback(async () => {
    try {
      const resp: any = await apiService.getCompanySettings();
      const data = resp?.data || resp;
      if (data?.settings) {
        setCompanyProfile({
          companyName: data.settings.companyName || '',
          description: data.settings.description || null,
          industry: data.settings.industry || null,
          services: data.settings.services || null,
        });
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    // Don't show onboarding until we have a user ID
    if (!user?.id) {
      setShowOnboarding(false);
      return;
    }
    // Check both user-specific and generic key (backward compat)
    const completed = localStorage.getItem(storageKey) || localStorage.getItem('onboarding_completed');
    if (!completed) {
      setShowOnboarding(true);
    } else {
      setShowOnboarding(false);
      // Ensure the user-specific key is set for future checks
      if (!localStorage.getItem(storageKey)) {
        localStorage.setItem(storageKey, completed);
      }
    }

    loadCompanyProfile().finally(() => setLoading(false));
  }, [user?.id, storageKey, loadCompanyProfile]);

  const completeOnboarding = useCallback(() => {
    localStorage.setItem(storageKey, new Date().toISOString());
    setShowOnboarding(false);
  }, [storageKey]);

  const isCompanyProfileComplete = Boolean(
    companyProfile?.companyName &&
    companyProfile?.description &&
    companyProfile?.industry &&
    companyProfile?.services
  );

  return {
    showOnboarding,
    completeOnboarding,
    isCompanyProfileComplete,
    companyProfile,
    reloadCompanyProfile: loadCompanyProfile,
    loading,
  };
}
